import { Alert, Snackbar } from "@mui/material";
import React, { createContext, useContext, useRef, useState } from "react";

type SnackbarType = "success" | "error" | "warning";

interface ShowMessageProps {
  message: string | null;
  severity: SnackbarType | null;
  displayMessage: (message: string, type: SnackbarType) => void;
}

export const SnackbarContext = createContext<ShowMessageProps>({
  message: null,
  severity: null,
  displayMessage: () => {},
});

export const useSnackbar = () => useContext(SnackbarContext);

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [message, setMessage] = useState<string | null>(null);
  const [severity, setSeverity] = useState<SnackbarType | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimeoutFuntion = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const displayMessage = (message: string, severity: SnackbarType) => {
    onClose();
    setMessage(message);
    setSeverity(severity);
    timeoutRef.current = setTimeout(() => {
      onClose();
    }, 5000);
  };

  const onClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason !== "clickaway") {
      clearTimeoutFuntion();
      setMessage(null);
      setSeverity(null);
    }
  };

  return (
    <SnackbarContext.Provider value={{ message, severity, displayMessage }}>
      {children}
      <Snackbar
        open={!!message}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={onClose}
          severity={severity || "success"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
